<template>
  <div>
    <div class="f-flex f-justify-between">
      <BaseRadio
        ref="radio"
        name="shipping_method"
        :value="shippingMethod.shippingMethodResource.id"
        :id="$id('shipping-' + shippingMethod.shippingMethodResource.id)"
        @input="shippingMethodChanged"
        v-model="selected"
        v-on:click="showPackateryWidget"
      >
        <span
          :aria-label="`${
            shippingMethod.shippingMethodResource.name
          }, cena: ${formattedPrice(price)}`"
        >
          {{ shippingMethod.shippingMethodResource.name }}
        </span>
        <ShippingPopover
          v-if="shippingDescription"
          :description="shippingDescription"
        ></ShippingPopover>
        <div v-show="freeOfCharge" class="f-text-sm">
          {{ formattedFreeOfCharge(freeOfCharge) }}
        </div>
      </BaseRadio>
      <div v-if="this.freeShipping">
        <div class="f-text-right f-text-xs f-line-through">
          {{ formattedPrice(price) }}
        </div>
        <div
          class="f-text-right"
          v-bind:class="{ 'f-text-red-600': this.freeShipping }"
        >
          {{ $t("prices.free").toUpperCase() }}
        </div>
      </div>
      <div v-if="!this.freeShipping">
        {{ formattedPrice(price) }}
      </div>
    </div>
    <ValidationProvider
      :rules="rulesObject"
      :skipIfEmpty="false"
      v-slot="{ errors }"
    >
      <div v-show="isSelected">
        <label>
          <select
            class="f-form-select f-border-gray-300 f-rounded f-w-full f-mt-2 f-py-1 f-text-gray-700"
            ref="select"
            :id="
              $id('shipping-select-' + shippingMethod.shippingMethodResource.id)
            "
            :name="
              shippingMethod.shippingMethodResource.id + '_packatery_branch_id'
            "
            v-model="selectedOption"
            v-on:click="showPackateryWidget"
          >
            <option
              v-for="option in options"
              :value="option.value"
              :key="option.value"
            >
              {{ option.text }}
            </option>
          </select>
        </label>
        <div v-if="getFirstError(errors)" class="f-text-sm f-text-red-600">
          {{ getFirstError(errors) }}
        </div>
      </div>
    </ValidationProvider>
  </div>
</template>

<script lang="ts">
import ScriptsLoader from "@/module/Utils/ScriptsLoader";
import FormShippingItem from "@/module/FormShippingItem";
import FormResource from "@/module/Form/Resources/FormResource";
import InputValidationMixin from "@/components/Form/Basic/Inputs/InputValidationMixin";
import mixins from "vue-typed-mixins";
import { ValidationProvider } from "vee-validate";
import Vue, { PropType } from "vue";
import {
  isObjectOrNull,
  isStringOrNull,
} from "@/module/ComponentPropsValidations/validations";
import BaseRadio from "@/components/Form/Basic/Inputs/BaseRadio.vue";
import money from "@/module/Filters/MoneyFilter";
import ShippingPopover from "@/components/Form/ShippingMethods/ShippingPopover.vue";
import OrderPrices from "@/module/Order/OrderPrices";
import NumberHelper from "@/module/NumberHelper";
import SelectedShippingMethod from "@/module/Shipping/SelectedShippingMethod";

interface ComponentData {
  selected: number | null;
  packateryLoaded: boolean;
  selectedOption: number | null;
  options: {
    value: number | null;
    text: string;
    carrierPickupPointId: string | null;
  }[];
  freeShipping: boolean;
}

export default mixins(InputValidationMixin).extend({
  name: "PackateryPersonalCollection",

  components: {
    ShippingPopover,
    ValidationProvider,
    BaseRadio,
  },

  props: {
    shippingMethod: {
      type: Object as PropType<FormShippingItem>,
      required: true,
    },
    currentlySelectedRadio: {
      type: null as unknown as PropType<SelectedShippingMethod | null>,
      required: true,
      validator: isObjectOrNull,
    },
    packateryApiKey: {
      required: true,
      validator: isStringOrNull,
    },
    currency: {
      type: String,
      required: true,
    },
    order: {
      type: Object as PropType<OrderPrices>,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    totalPriceIncVat: {
      required: true,
    },
  },

  data(): ComponentData {
    return {
      selected: null,
      packateryLoaded: false,
      selectedOption: null,
      options: [],
      freeShipping: false,
    };
  },

  computed: {
    freeOfCharge(): number | null {
      if (this.shippingMethod.freeOfCharges.hasOwnProperty("noFreeShipping")) {
        return null;
      }

      const shipping_form_item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      if (shipping_form_item === undefined) {
        return null;
      }

      const price = this.shippingMethod.freeOfCharges[this.currency];
      const includingVat =
        this.shippingMethod.shippingMethodResource.including_vat;
      const currentVat = shipping_form_item.currentVat;

      if (this.formResource.show_prices_including_vat) {
        return NumberHelper.priceIncludingVat(price, currentVat, includingVat);
      }
      return NumberHelper.priceWithOutVat(price, currentVat, includingVat);
    },

    isSelected: function (): boolean {
      return (
        this.shippingMethod.shippingMethodResource.id ===
        this.currentlySelectedRadio?.shipping_method_id
      );
    },

    rulesObject(): object {
      return this.isSelected ? { required: true } : {};
    },

    price(): number {
      const item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      if (item === undefined) {
        return 0;
      }

      if (this.freeOfCharge !== null) {
        this.updateFreeShipping(this.isEligibleForFreeShipping());
      }

      if (this.formResource.show_prices_including_vat) {
        return item.currentPriceIncVat;
      }

      return item.currentPrice;
    },

    shippingDescription(): string | null {
      return this.shippingMethod.shippingMethodResource.description;
    },
  },

  methods: {
    formattedFreeOfCharge(price: number): string {
      if (price) {
        return (
          String(this.$t("shipping_method.free_shipping_from")) +
          money(price, this.$root.$i18n)
        );
      }
      return "";
    },

    updateFreeShipping(value: boolean): void {
      this.freeShipping = value;
    },

    isEligibleForFreeShippingHelper(vat: boolean): number {
      if (vat) {
        return this.order.totalPriceWoShippingIncludingVat;
      }
      return this.order.totalPriceWoShipping;
    },

    isEligibleForFreeShipping(): boolean {
      if (this.freeOfCharge !== null) {
        if (
          this.isEligibleForFreeShippingHelper(
            this.formResource.show_prices_including_vat,
          ) >= this.freeOfCharge
        ) {
          return true;
        }
      }
      return false;
    },

    formattedPrice(price: number): string {
      return money(price, this.$root.$i18n);
    },
    showPackateryWidget(): void {
      if (!this.packateryLoaded) {
        return;
      }

      const countryCodes =
        this.shippingMethod.shippingMethodResource.address_prices.map(
          (item) => item.address.country_code,
        );

      // @ts-ignore
      Packeta.Widget.pick(this.packateryApiKey, this.branchSelected, {
        appIdentity: "fapi",
        language: this.$root.$i18n.locale,
        country: countryCodes.join(",").toLowerCase(),
      });

      const widget = document.getElementById("packeta-widget");

      if (widget) {
        widget.style.padding = "30px";
      }
    },

    shippingMethodChanged(): void {
      const branch = this.options.find((item) => {
        return item.value === this.selectedOption && item.value !== null;
      });

      this.$emit("shippingMethodChanged", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        shipping_metadata: {
          packatery_branch_name: branch ? branch.text : null,
          packatery_branch_id: this.selectedOption,
          packatery_carrier_pickup_id: branch?.carrierPickupPointId ?? null,
        },
        shipping_form_item: this.shippingMethod.formItems.find((item) => {
          return item.name === this.country;
        }),
      } as SelectedShippingMethod);
      this.$emit("isEligibleForFreeShipping", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
      });
    },

    branchSelected(
      data: {
        id: string;
        name: string;
        pickupPointType: string;
        carrierPickupPointId: string | null;
        carrierId: string | null;
      } | null,
    ): void {
      if (!data) {
        return;
      }
      const isExternal = data.pickupPointType === "external";

      const value = Number(isExternal ? data.carrierId : data.id);
      const text = data.name;
      const carrierPickupPointId = isExternal
        ? (data.carrierPickupPointId ?? null)
        : null;

      this.options = [
        {
          value,
          text,
          carrierPickupPointId,
        },
      ];

      this.selectedOption = value;
      this.shippingMethodChanged();
    },

    triggerVisibility(): void {
      const item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      const visible = item !== undefined;
      this.$emit("shippingMethodVisibilityChanged", { visible: visible });
    },
  },

  watch: {
    totalPriceIncVat(): void {
      if (this.selected) {
        this.$emit("isEligibleForFreeShipping", {
          shipping_method_id: this.shippingMethod.shippingMethodResource.id,
          is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
        });
      }
    },

    country(): void {
      this.triggerVisibility();
      this.$emit("shippingMethodChanged", null);
      this.selected = null;
      this.selectedOption = null;
    },
  },

  created(): void {
    this.triggerVisibility();
  },

  mounted(): void {
    ScriptsLoader.loadScript(
      "https://widget.packeta.com/v6/www/js/library.js",
      () => {
        this.packateryLoaded = true;
      },
    );

    const selectComponent = this.$refs.select as HTMLElement;
    const radioComponent = this.$refs.radio as Vue;

    selectComponent.addEventListener("focus", this.showPackateryWidget);
    radioComponent.$el.addEventListener("click", this.showPackateryWidget);

    this.options.push({
      value: null,
      text: String(this.$t("shipping_method.packatery_branch.prompt")),
      carrierPickupPointId: null,
    });
  },
});
</script>

<style lang="scss"></style>
